import { formatNumber } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy, effect, inject, signal } from '@angular/core';
import { VANILLA_STRIP } from '@approot/constants';
import { GlobalWebsocketService } from '@approot/global-websocket';
import {
  AdjustQuoteForMargin,
  AttributeBase,
  BankState,
  DiscountFactorResponse,
  GenericLeg,
  KeyValue,
  LegPrice,
  MedusaCodeAndType,
  MultiHedge,
  QuoteSide,
  RFQ_PROVIDER_STATE_DECLINED,
  RFQ_PROVIDER_STATE_DONE,
  RFQ_PROVIDER_STATE_INTERVENTION,
  RFQ_PROVIDER_STATE_PICKED_UP,
  RFQ_PROVIDER_STATE_UNQUOTED,
  RFQ_QUOTE_CONFIRM_RFE_ACCEPT,
  RFQ_QUOTE_CONFIRM_RFE_DECLINE,
  RFQ_QUOTE_CONFIRM_RFE_NOT_SENT,
  RFQ_QUOTE_CONFIRM_RFE_SENT,
  RFQ_STATUS_BLOCKED,
  RFQ_STATUS_CANCELLED,
  RFQ_STATUS_EXPIRED,
  RFQ_STATUS_STREAMING,
  RFQ_STATUS_TRADED,
  RfeAction,
  RfqBidOfferQuotes,
  RfqQuote,
  RfqQuoteState,
  RfqSettingsState,
  RfqSettingsStateKey,
  RfqSetup,
  RfqStatus,
  RfqTab,
  RfqTabLeg,
  RfqTabLegs,
  SubAccount,
  UserSettings
} from '@approot/models';
import {
  TabItem,
  VolBasedRfqOptionPriceV2Response
} from '@approot/provider-quote';
import {
  AlertsService,
  EntityMapperService,
  HedgePayload,
  PhoenixService,
  TabServiceRegistryService,
  UserStorageService
} from '@approot/services';
import {
  createQuote,
  mungeQuotes
} from '@approot/shared/custom-components/rfq-quotes/quotes';
import {
  SubscriptionSink,
  Utilities,
  clone,
  dvSchemas,
  pipe,
  reformatDate,
  sleep,
  uuid
} from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import { isQuoteOutdated } from '@approot/widgets/rfq/rfq-container/rfq-tab/rfq-setup/rfq-setup';
import { RfqSetupService } from '@approot/widgets/rfq/rfq-container/rfq-tab/rfq-setup/rfq-setup.service';
import { RfqTabStateService } from '@approot/widgets/rfq/rfq-container/rfq-tab/rfq-tab-state.service';
import { getAttributeControlFromKey } from '@approot/widgets/strategy-detail/leg/utilities/leg-attribute-utilities';
import {
  setAllFieldsReadOnlyState,
  setAttributeReadOnly
} from '@approot/widgets/strategy-detail/leg/utilities/leg.utilities';
import dayjs from 'dayjs';
import { isEqual, pick } from 'lodash';
import { Debounce } from 'lodash-decorators';
import { cloneDeep, filter, forEach, reverse, uniqBy } from 'lodash/fp';
import { Observable, Subject, Subscription, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { AdminService } from '../../admin/admin.service';
import {
  ConfigService,
  TradeBookingParameters
} from '../../config/config.service';
import { LegService } from '../../leg/leg.service';
import { ProductsConfigService } from '../../products-config/products-config.service';
import { RfqService } from '../../rfq/rfq.service';
import { TradesCaptureService } from '../../trade-capture/trades-capture.service';
import {
  Account,
  AdjustQuoteForMarginPayload,
  BsOption,
  ClientEventQuoteExtended,
  ClientEventQuoteExtendedV2,
  ClientQuoteEventExtended,
  ClientQuoteEventV2,
  DcdMarginForOptionPrice,
  Description,
  ExecutionType,
  HedgeMode,
  HedgeProduct,
  HedgeQuoteMessage,
  IndicativeQuotes,
  KeyValueResponse,
  MappedProviderList,
  MidPriceInfo,
  NewVolsPayload,
  PortfolioResult,
  PortfolioResultV2,
  RfqHedgeQuote,
  Timer,
  ValidateMultiHedgeRFQResponse,
  VolBasedHedgeExecutionPayload,
  VolBasedHedgeExecutionPayloadGuiEvent,
  VolBasedRfqOptionPricePayload,
  VolBasedRfqOptionPricePayloadTask,
  VolBasedRfqOptionPriceV2Result
} from '../rfq.types';
import {
  Product,
  RfqOutcome,
  ShelvedQuote,
  allFaceCurrenciesTheSame,
  countdownColorCssClass,
  deepCopyNdp,
  getHedgeType,
  getReferenceCounterNotional,
  getReferenceNotional,
  getRfqDto,
  getStrategy,
  midPrice,
  rfqProductApiName,
  setVols,
  setVolsForwards,
  validProduct
} from './rfq-tab';

type OptionLegDTO = dvSchemas['OptionLegDTO'];
export type RFQDetailsDTO = dvSchemas['RFQDetailsDTO'] & {
  is_dcd?: boolean;
  indicative?: boolean;
};

export type RFQResponseDTO = dvSchemas['RFQDetailsDTO'];

interface RfqOutcomeModal {
  showModal: boolean;
  requestId: number;
}
type PrimeBrokers = dvSchemas['AccountAvailableParties'];

export type SetupEventChange =
  | 'hedgeRate'
  | 'execution'
  | 'spotRefMethod'
  | 'hedgeCustomAmount'
  | 'other';

type SetupEventOptions = {
  change: SetupEventChange;
  isNew?: boolean;
  legs?: any;
};

const { formattedNumberToNumber } = Utilities;

@Injectable({
  providedIn: 'root'
})
export class RfqTabService implements OnDestroy {
  public accounts = signal<Account[]>([]);
  public legsValid = true;
  public executionType: ExecutionType;
  public executedSide: string;
  public rfeRequestInfo: RfeAction; // the status of an RFE request
  public selectedLegs: LegComponent[]; // the legs that are selected are stored here
  public rfqDetailsDTO: RFQDetailsDTO;
  public tabData: RfqTab | TabItem;
  public rfqRunning = signal<boolean>(false);
  public mainTimer: Timer;
  public primeBrokers: PrimeBrokers;
  public providers: MappedProviderList;
  public animateQuotes = true;
  public rfeTimer: any;
  public forwardPointsMultiplier = signal<number>(null);
  public selectedBanksState = signal<BankState[]>([]);
  public clientEntity: string;
  public isDirectRfq = false;
  public traded = false;
  public formattedDtoLegs: RFQDetailsDTO[] = [];
  public medusaCodeAndType: MedusaCodeAndType;
  public createDtoResponse: RFQDetailsDTO;
  public lastHandleVolBasedHedgeExecution =
    signal<TradeBookingParameters>(null);
  public selectedSubAccount: SubAccount;
  public changeInHedges = new Subject<void>();
  public newVolBasedQuoteListeners: Function[] = [];
  public lastNewVolBasedQuoteResponse: VolBasedRfqOptionPriceV2Response;
  public legFormsInert = signal<boolean>(false);
  public lastQuoteEvents = signal<Map<number, ClientQuoteEventExtended>>(
    new Map<number, ClientQuoteEventExtended>()
  );
  public rfqOutcomeModal = signal<RfqOutcomeModal>({
    showModal: false,
    requestId: null
  });
  public indicativeQuotes = signal<IndicativeQuotes>({
    bid: { quote: null, loading: false },
    offer: { quote: null, loading: false }
  });
  public executedTradeInfo: {
    legs: GenericLeg[];
    setup: RfqSetup;
    hedges: MultiHedge[];
    confirmRFEEvent: ClientQuoteEventExtended;
  } = null;
  public quotes: RfqBidOfferQuotes = {
    bids: [],
    offers: []
  };
  public rfqDetailsDTOMapped: RFQDetailsDTO;
  public windowCloser = new Subject<void>();
  public canCloseWindow = true;
  public warnOnClose = false;
  public setupMultiHedgesValidating = false;
  public onRfqSessionEnded = new Subject<void>();
  public rfqCommentSignal = signal('');
  public lastVolBasedRfqOptionPriceV2Response =
    signal<VolBasedRfqOptionPriceV2Result>(null);
  public midPriceInfo = signal<MidPriceInfo>(null);
  public hedgeQuoteMessages = signal<Map<string, HedgeQuoteMessage>>(
    new Map<string, HedgeQuoteMessage>([])
  );
  public shelvedQuotes = new Map<string, ShelvedQuote>([]);
  public pricingResult = signal<LegPrice>(null);
  public statusTypeUpdated = signal<RfqStatus>(null);

  private entityMapperService = inject(EntityMapperService);
  private tabServiceRegistryService = inject(TabServiceRegistryService);
  private id = Symbol('RfqTabService');
  private _eventQuoteRanking: any;
  private _frozenQuotes: RfqBidOfferQuotes;
  private rfqDetailsDTOMappedInitial: RFQDetailsDTO;
  private lastBsOption: BsOption;
  private clientShortName: string;
  private userSettings: UserSettings;
  private subsink = new SubscriptionSink();
  private legsAttributeControls: AttributeBase<any>[][];
  private defaultAnimateQuotes = true;

  constructor(
    private rfqService: RfqService,
    private legService: LegService,
    private adminService: AdminService,
    private tradesCaptureService: TradesCaptureService,
    private productsConfigService: ProductsConfigService,
    private configService: ConfigService,
    private rfqState: RfqTabStateService,
    private userStorageService: UserStorageService,
    private rfqSetupService: RfqSetupService,
    private phxService: PhoenixService,
    private websocketService: GlobalWebsocketService,
    private alertsService: AlertsService
  ) {
    this.subsink.sink = this.setupPollEventResponse();
    this.tabServiceRegistryService.register(this.id, this);

    effect(() => {
      const userSettings = this.configService.userSettings();
      this.userSettings = userSettings;
      if (userSettings.parent.useSingleProviderDetailedView === true) {
        this.defaultAnimateQuotes = false;
      }
      this.animateQuotes = this.defaultAnimateQuotes;
    });

    this.hedgeQuotesListener();
  }

  public ngOnDestroy(): void {
    this.destroy();
    this.windowCloser.complete();
    console.log('Tab Service Destroyed');
  }

  public destroy() {
    this.subsink.unsubscribe();
    this.tabServiceRegistryService.deregister(this.id);

    if (this.rfqRunning()) {
      this.cancelRfq();
    }

    if (this.rfeTimer) {
      clearTimeout(this.rfeTimer);
    }
  }

  public closeWindow() {
    this.windowCloser.next();
  }

  public setupPollEventResponse(): Subscription {
    // Only want events for the current RFQ (or globalEvents which are sent to all tabs)

    return this.rfqService.pollEventResponse$.subscribe((events: any[]) => {
      if (events.length === 0) {
        return;
      }
      this.handleEvents(events);
    });
  }

  public handleEvents(events: any[]): void {
    const filterEvent = (event: any) => {
      const id = this.rfqDetailsDTO?.id;
      const requestId =
        event.quote?.requestId ?? event.requestId ?? event.dto?.requestId;

      return (
        !event.processed &&
        id != null &&
        (id === requestId || event['@type'] === 'GlobalEvent')
      ); // globalEvents are events sent to all tabs
    };

    const processEvent = (event: any) => {
      if (event['@type'] !== 'GlobalEvent') {
        this.rfqService.markEventsAsProcessedById(event.id);
        event.processed = true;
      }
      // we go through each event and carry out the events relative to the tab.
      // we do it this way to prevent race conditions, as this may occur if pushing them from the main rfq service.
      switch (event['@type']) {
        case 'ClientQuoteEvent':
          this.handleClientQuoteEvent(event);
          break;
        case 'QuoteRankEvent':
          this.handleQuoteRankEvent(event, true);
          break;
        case 'RequestSummaryEvent':
          this.handleRequestSummaryEvent(event);
          break;
        case 'GlobalEvent':
          this.handleGlobalEvent(event);
          break;
      }
      return event;
    };

    const uniqEvent = (event: any) => {
      let uniqId: number | string;
      switch (event['@type']) {
        case 'ClientQuoteEvent':
          uniqId = event.quote?.providerId;
          if (uniqId == null) {
            console.warn('ClientQuoteEvent missing providerId');
            uniqId = uuid();
          }
          break;
        case 'QuoteRankEvent':
          // Only care about the last quote rank event so use a fixed string
          uniqId = '-';
          break;
        // case 'RequestSummaryEvent':
        //   break;
        // case 'GlobalEvent':
        //   break;
        default:
          // by default process all events
          uniqId = uuid();
      }
      return `${uniqId}/${event['@type']}`;
    };

    // the getEvents "batch" can contain multiple similar events from the same provider.
    // Here we try to find just the last one by (type, provider)
    // uniqBy only keeps the first one so we reverse the array
    pipe(
      filter(filterEvent),
      reverse,
      uniqBy(uniqEvent),
      reverse, // probably not needed but no harm
      forEach(processEvent)
    )(events);
  }

  public get frozenQuotes(): RfqBidOfferQuotes {
    return this._frozenQuotes;
  }

  public clearOldQuotes() {
    this.quotes = {
      bids: [],
      offers: []
    };
    this._frozenQuotes = clone(this.quotes);
  }

  public set frozenQuotes(quotes: RfqBidOfferQuotes) {
    // set a screenshot of the current quotes
    if (quotes) {
      // if there are quotes only keep frozen the ones where the timestamps are valid
      quotes.bids = quotes.bids.filter(
        bid =>
          bid.timeLimit >= this.rfqService.currentTimestamp || !bid.timeLimit
      );
      quotes.offers = quotes.offers.filter(
        offer =>
          offer.timeLimit >= this.rfqService.currentTimestamp ||
          !offer.timeLimit
      );

      [...quotes.bids, ...quotes.offers].forEach(
        quote => (quote.frozenQuote = true)
      );
    }
    this._frozenQuotes = quotes;
  }

  public get quotesBeingDisplayed() {
    // if there is a screenshot of quotes use that
    return this.frozenQuotes ?? this.quotes;
  }

  public get mungedDisplayedQuotes(): RfqBidOfferQuotes {
    return mungeQuotes(this.quotesBeingDisplayed);
  }

  public maybeAnimateQuotes(state?: boolean): void {
    if (state == null || state) {
      this.animateQuotes = this.defaultAnimateQuotes;
    } else {
      this.animateQuotes = state;
    }
  }

  private handleGlobalEvent(event: { '@type': string; action: string }) {
    switch (event.action) {
      case 'blockTab':
        // block the tab by cancelling it first
        this.rfqRunning.update(_ => false);
        this.wipeAllTimers();
        this.cancelRfq();
        this.changeTabTile(RFQ_STATUS_BLOCKED, null);
        break;
    }
  }

  private handleRequestSummaryEvent(event: dvSchemas['RequestSummaryEvent']) {
    this.clientShortName = event.dto.clientShortName;
    event.quotes.forEach(quote => {
      this.updateProviderState(quote, null);
    });
  }

  public handleClientQuoteEventUsingLast(_reason: string): void {
    if (!this.rfqRunning()) {
      return;
    }
    // console.log('handleClientQuoteEventUsingLast', _reason);
    this._handleClientQuoteEventUsingLast();
  }

  @Debounce(500)
  private _handleClientQuoteEventUsingLast(): void {
    const lastQuotesEvents = this.lastQuoteEvents();
    if (lastQuotesEvents?.size > 0) {
      [...lastQuotesEvents.values()].forEach(quote => {
        this.handleClientQuoteEvent(quote);
      });
    }
  }

  public handleClientQuoteEvent(event: ClientQuoteEventExtended): void {
    // if (this.tabData.isVolBased) {
    //   // when vols are quoted, medusa sends the bid/offer updated in sep.
    //   // events - the 1st one might be in an invalid state, so filter.
    //   const volsOK = event.quote?.legs.every((leg, i) => {
    //     const { volBid, volOffer } = leg;
    //     const inverted = leg.inverted;
    //     if (volBid != null && volOffer != null) {
    //       if (!inverted && volBid > volOffer) {
    //         console.log(
    //           `Filtering invalid client event: volBid ${volBid} > volOffer ${volOffer} [leg ${i}, not inverted]`
    //         );
    //         return false;
    //       } else if (inverted && volBid < volOffer) {
    //         console.log(
    //           `Filtering invalid client event: volBid ${volBid} < volOffer ${volOffer} [leg ${i}, inverted]`
    //         );
    //         return false;
    //       }
    //     }
    //     return true;
    //   });

    //   if (!volsOK) {
    //     return;
    //   }
    // }

    switch (event.action) {
      case 'Declined':
      case 'ConfirmRFEDecline':
        // response from an rfe
        this.updateProviderState(event.quote, RFQ_PROVIDER_STATE_DECLINED);
        this.recordRfqOutcome('DECLINED');
        if (event.quote) {
          if (event.action === 'ConfirmRFEDecline') {
            this.handleRfeResponse(event);
          }
          // if no rfe status and the rfq is running
          this.updateProviderState(event.quote, RFQ_PROVIDER_STATE_DECLINED);
          this.removeBankQuote(this.quotes.bids, event.quote.provider);
          this.removeBankQuote(this.quotes.offers, event.quote.provider);
        }

        if (this.providers.length === 1) {
          const message =
            event.action === 'Declined' ? 'RFQ declined' : 'RFE declined';
          this.alertsService.addAlert({
            message,
            type: 'warning',
            skipLog: true
          });
          this.wipeAll();
        }
        break;
      case 'PickedUp':
        this.updateProviderState(event.quote, RFQ_PROVIDER_STATE_PICKED_UP);
        break;
      case 'ConfirmWithdraw':
        this.removeBankQuote(this.quotes.bids, event.quote.provider);
        this.removeBankQuote(this.quotes.offers, event.quote.provider);
        break;
      case 'QuoteDelay':
        this.handleDealerIntervention(event);
        break;
      case 'ConfirmQuoteSubmit':
        this.handleQuoteResponse(event);
        break;
      case 'ConfirmRFEAccept':
        this.traded = true;
        this.saveExecutedTradeInfo(event);
        this.revertLegControlAttributes();
        this.recordRfqOutcome('TRADED');
        if (this.tabData.isVolBased) {
          this.handleQuoteAcceptForVolBased(event);
        } else {
          this.tradesCaptureService.tradeDone();
        }
        if (event.quote) this.handleRfeResponse(event);
        break;
    }
  }

  private saveExecutedTradeInfo(
    confirmRFEEvent: ClientQuoteEventExtended
  ): void {
    const legs = this.createProductsObject();
    const setup = this.rfqSetupService.formatSetup(this.setupData);
    const hedges = clone(this.rfqSetupService.setup.getHedges()?.hedges || []);
    this.executedTradeInfo = { legs, setup, hedges, confirmRFEEvent };
    this.tabServiceRegistryService.register(this.id, this);
  }

  private handleDealerIntervention(event: ClientQuoteEventExtended): void {
    const sendDealerInterventionToPerseus =
      this.configService.userSettings().parent[
        'trading:sendDealerInterventionToPerseus'
      ];
    this.updateProviderState(event.quote, RFQ_PROVIDER_STATE_INTERVENTION);
    if (sendDealerInterventionToPerseus) {
      this.rfqService.sendDealerIntervention(event).subscribe();
    }
  }

  private handleQuoteAcceptForVolBased(event: ClientQuoteEventExtended) {
    let executedSide: 'Bid' | 'Ask';
    if (this.executedSide === 'bid') {
      executedSide = 'Bid';
    } else if (this.executedSide === 'offer') {
      executedSide = 'Ask';
    }

    const guiEvent: VolBasedHedgeExecutionPayloadGuiEvent = {
      clientShortName: this.clientShortName,
      executedSide,
      premiumCurrency: this.rfqSetupService.setupForm.get('premium').value,
      premiumValue: this.rfqSetupService.setupForm.get('premiumValue').value,
      requester: this.adminService.medusaAccount.userId,
      clientEntity: this.clientEntity,
      isBlockTrade:
        this.rfqSetupService.setupForm.get('isBlockTrade').value === 'Yes'
    };

    const payload: VolBasedHedgeExecutionPayload = {
      medusaEvent: event,
      guiEvent,
      pricingDetails: this.lastNewVolBasedQuoteResponse || this.lastBsOption,
      hedgeMode: this.rfqSetupService.setup.hedgeMode || 'None'
    };

    console.log(`handleVolBasedHedgeExecution: initiating`);
    this.rfqService.handleVolBasedHedgeExecution(payload).subscribe({
      next: r => {
        this.phxService.logEvent({
          type: 'hydra',
          category: 'response',
          payload,
          response: r,
          task: 'handleVolBasedHedgeExecution'
        });
        console.log(`handleVolBasedHedgeExecution: success`);
        this.tradesCaptureService.tradeDone();
        this.lastHandleVolBasedHedgeExecution.set(r);
      },
      error: err => {
        console.error(`handleVolBasedHedgeExecution: error`, err);
        this.phxService.logEvent({
          type: 'hydra',
          category: 'error',
          payload,
          error: err,
          task: 'handleVolBasedHedgeExecution'
        });

        console.log(err);
      }
    });
  }

  private handleQuoteRankEvent(
    event: dvSchemas['QuoteRankEvent'],
    saveEvent?: boolean
  ) {
    // order quotes based on rankings from backend, only if an rfe has not been sent
    this.quotes.bids.sort(function (a, b) {
      return event.bidIds.indexOf(a.id) - event.bidIds.indexOf(b.id);
    });

    this.quotes.offers.sort(function (a, b) {
      return event.offerIds.indexOf(a.id) - event.offerIds.indexOf(b.id);
    });

    if (saveEvent) this._eventQuoteRanking = event; // we save the event ranking to use later
  }

  private handleRfeResponse(event: ClientQuoteEventExtended) {
    // the response from the rfe is handled here
    clearTimeout(this.rfeTimer); // rfe timer can be cleared since we got a response back
    const quoteSide = this.rfeRequestInfo.type === 'offer' ? 'offers' : 'bids';
    this.animateQuotes = false; // disable the animations
    const quoteSent = this.quotesBeingDisplayed[quoteSide].find(
      quoteObj => quoteObj.providerId === this.rfeRequestInfo.providerId
    );
    switch (event.action) {
      case 'ConfirmRFEAccept': {
        // if the rfe was accepted
        this.rfeRequestInfo.status = event.action;
        this.rfqRunning.update(_ => false); // rfq is not running
        // only show animations once the latest quotes have been loaded on screen.
        this.animateQuotes = this.defaultAnimateQuotes;
        quoteSent.status = RFQ_QUOTE_CONFIRM_RFE_ACCEPT; // set the status of the quote
        this.tabData.title.status.statusType = RFQ_STATUS_TRADED;
        this.statusTypeUpdated.set(RFQ_STATUS_TRADED);
        this.tabData.title.status.statusExtra = null;
        this.revertLegControlAttributes();
        break;
      }
      case 'ConfirmRFEDecline': {
        // if the quote was declined
        quoteSent.status = RFQ_QUOTE_CONFIRM_RFE_DECLINE; // set the decline status
        quoteSent.timeLimit = null; // remove the timelimit to keep it on screen
        this.frozenQuotes = null; // show latest quotes
        const quotes = this.quotes[quoteSide];
        let latestQuoteIndex = quotes.findIndex(
          quoteObj => quoteObj.providerId === this.rfeRequestInfo.providerId
        ); // get the most up to date quote

        setTimeout(() => {
          // only show animations once the latest quotes have been loaded on screen.
          this.animateQuotes = this.defaultAnimateQuotes;
          if (latestQuoteIndex !== -1) {
            quotes[latestQuoteIndex] = clone(quoteSent); // swap latest quote with declined quote
          } else {
            // quote has been removed, so re add it.
            quotes.push(quoteSent);
          }
          this.removeQuoteFromBidOrOfferAfterInterval(
            quoteSide,
            this.rfeRequestInfo.providerId
          ); // set the declined quote after 3 seconds
        });
        this.startTimer(); // restart the main timer
        this.clearRfeRequestInfo();
        break;
      }
    }
  }

  public clearRfeRequestInfo() {
    setTimeout(() => (this.rfeRequestInfo = null));
  }

  private removeQuoteFromBidOrOfferAfterInterval(
    side: string,
    providerId: number
  ) {
    // remove a quote based on the side and the providerId after 3 seconds
    setTimeout(() => {
      this.quotes[side] = (this.quotes[side] as RfqQuote[]).filter(
        quoteObj => quoteObj.providerId !== providerId
      );
    }, 3000);
  }

  private updateProviderState(quote: any, status: RfqQuoteState) {
    // update the states of selected providers when a response is received
    if (
      this.tabData.title.status.statusType !== RFQ_STATUS_TRADED &&
      this.tabData.title.status.statusType !== RFQ_STATUS_EXPIRED
    ) {
      this.selectedBanksState.update(state => {
        const bankState = state.find(bank => bank.id === quote.providerId);
        if (bankState) {
          if (status) {
            bankState.status = status;
          }
          bankState.quoteId = quote.id || quote.quoteId;
        }

        return clone(state);
      });
    }
    this.rfqState.update({
      rfqStatus: this.tabData.title.status.statusType
    });
  }

  private mainCountDown(time: number) {
    // the main big timer starts at 5 mins and counts down.
    this.mainTimer = { timer$: null, interval: null, checkPoint: null };
    this.mainTimer.timer$ = new Observable(observer => {
      observer.next(time); // we dont want to wait a full second before pushing the first timeout
      this.mainTimer.interval = setInterval(() => {
        this.mainTimer.checkPoint = countdownColorCssClass(time);
        if (time <= 0) {
          // once the timer hits 0, wipe all timers and clear all data stored
          observer.next('Expired');
          this.changeTabTile(RFQ_STATUS_EXPIRED, null);
          this.openNotTradingReasonModal();
          this.revertLegControlAttributes();
          this.wipeAll();
        } else {
          time--;
          observer.next(time);
          this.changeTabTile(RFQ_STATUS_STREAMING, time);
        }
      }, 1000);
    });
  }

  public wipeAllTimers(): void {
    // when all timers need to be wiped
    if (this.mainTimer) {
      clearInterval(this.mainTimer?.interval);
      this.mainTimer.timer$ = null;
    }

    this.clearRfeTimer();
  }

  public clearRfeTimer(): void {
    if (this.rfeTimer) {
      clearTimeout(this.rfeTimer);
    }
  }

  public wipeAll(sendCancelMsg = true): void {
    this.wipeAllTimers();
    this.rfqRunning.update(_ => false); // indicate that the rfq is not pricing anymore
    this.tabData.banksActive = 0; // no banks are active
    this.cancelRfq(sendCancelMsg);
  }

  public changeTabTile(statusType: RfqStatus, extraInfo: number) {
    this.tabData.title.status.statusType = statusType;
    this.tabData.title.status.statusExtra = extraInfo;
    this.statusTypeUpdated.set(statusType);
  }

  public startTimer() {
    const timeLeft = Math.floor(
      (this.rfqDetailsDTO.timeLimit - dayjs.utc().valueOf()) / 1000
    );
    this.rfqRunning.update(_ => true);
    this.mainCountDown(timeLeft);
  }

  private handleQuoteResponse(quoteEvent: ClientQuoteEventExtended) {
    this.lastQuoteEvents.update(events => {
      const clonedEvents = new Map([...events]);
      clonedEvents.set(quoteEvent.quote.providerId, quoteEvent);
      return clonedEvents;
    });

    if (!this.rfeRequestInfo) {
      // only change title if there are no rfes set
      this.tabData.title.status.statusType = RFQ_STATUS_STREAMING;
      this.statusTypeUpdated.set(RFQ_STATUS_STREAMING);
    }
    this.preBuildQuote(quoteEvent);
  }

  public reBuildQuotes(): void {
    this.lastQuoteEvents().forEach(quote => {
      const currentTime = dayjs.utc().valueOf();
      if (quote.quote.timelimit >= currentTime) {
        this.preBuildQuote(quote);
      }
    });
  }

  private preBuildQuote(quote: ClientQuoteEventExtended): void {
    const { hedges } = this.rfqSetupService.setup?.getHedges() || {
      hedges: []
    };
    this.buildBidOfferQuote(
      quote,
      hedges,
      this.rfqSetupService.setup?.hedgeMode
    );
  }

  private buildBidOfferQuote(
    quoteEvent: ClientQuoteEventExtended,
    hedges: any[],
    hedgeMode: HedgeMode
  ) {
    const rfqTabShortName = this.tabData.title.productName.shortName;
    quoteEvent.quote.timestamp = new Date().getTime(); // timestamp the quote came in

    if (
      !quoteEvent.quoteAdjusted &&
      this.tabData.useWhiteLabelMargin &&
      this.selectedLegs.every(x => x.form.get('margin')?.valid)
    ) {
      this.adjustQuoteForMargin(quoteEvent);
      return;
    }

    if (this.executionContextType() === 'genericMultiHedge') {
      setVolsForwards(
        quoteEvent.quote,
        this.tabData.title.productName.shortName,
        this.selectedLegs,
        this.forwardPointsMultiplier()
      );
    }

    switch (rfqTabShortName) {
      case 'dcd':
        this.formatDcdQuote(quoteEvent.quote, 'bid');
        break;
      default:
        if (this.tabData.isVolBased) {
          setVols(
            quoteEvent.quote,
            this.tabData.title.productName.shortName,
            this.selectedLegs
          ); // update the vols on the legs
          if (
            this.selectedLegs.every(leg => {
              const { forwardPointsAsk, forwardPointsBid } = leg.form.value;
              return (
                Utilities.cleanFormattedNumeric(forwardPointsAsk) >=
                Utilities.cleanFormattedNumeric(forwardPointsBid)
              );
            })
          ) {
            this.formatNewVolBasedQuote(quoteEvent, hedgeMode, hedges || []); // updated vols are sent
          }
        } else {
          const vols = quoteEvent.quote.legs.map(
            leg => ((leg.volBid + leg.volOffer) / 2) * 100
          );
          const bidQuote = this.buildQuote(
            quoteEvent.quote,
            'bid',
            null,
            null,
            null,
            null,
            null
          );
          const bidInserted = this.insertQuote(
            quoteEvent.quote,
            bidQuote,
            'bid',
            false
          );
          const offerQuote = this.buildQuote(
            quoteEvent.quote,
            'offer',
            null,
            null,
            null,
            null,
            null
          );
          const offerInserted = this.insertQuote(
            quoteEvent.quote,
            offerQuote,
            'offer',
            false
          );

          if (bidInserted || offerInserted) {
            this.setMidPriceInfo(quoteEvent.quote.premiumAmountMid, vols);
          }
        }
        break;
    }
  }

  private buildNewVolBasedQuote(
    task: VolBasedRfqOptionPricePayloadTask,
    hedgeMode: HedgeMode,
    hedges: HedgePayload[],
    requestId: number,
    quoteOrValidate: 'quote' | 'validate'
  ) {
    const products = this.createProductsObject() as HedgeProduct[];
    const { premiumValue } = this.setupData;
    const premiumDate = reformatDate(this.setupData.premiumDate, 'YYYY-MM-DD');
    const executionContext = this.tabData.executionContext;
    let payload: VolBasedRfqOptionPricePayload = {
      task,
      hedgeMode,
      premiumDate,
      premiumValue,
      premiumCurrency: this.setupData.premium,
      hedges,
      products,
      requestId,
      deltaHedgeRounding: executionContext.deltaHedgeRounding,
      enableDeltaHedgeRounding: executionContext.enableDeltaHedgeRounding,
      enablePremiumRounding: executionContext.enablePremiumRounding,
      premiumBpRounding: executionContext.premiumBpRounding,
      premiumStyle: this.setupData.premiumStyle,
      comment: this.rfqCommentSignal()
    };

    const spots = this.rfqSetupService.setupForm.get('spots')?.value;
    if (spots) {
      payload = { ...payload, ...spots };
    }
    // Temp workaround until new validate API is implemented (use old payload structure)
    if (quoteOrValidate === 'validate') {
      payload.internalHedges = hedges;
      delete payload.hedges;
      delete payload.hedgeMode;
    }
    return payload;
  }

  public registerNewVolBasedQuoteListener(fn: Function) {
    this.newVolBasedQuoteListeners.push(fn);
  }

  public removeVolBasedQuoteListener(index: number): void {
    this.newVolBasedQuoteListeners.splice(index, 1);
  }

  public updateNewVolBasedQuoteListeners(response: any) {
    this.newVolBasedQuoteListeners.forEach(fn => fn(response));
  }

  public removeAllVolBasedQuoteListeners(): void {
    /* everytime a hedge is added/removed new listeners
    are created so previous hedge listeners should be removed
    */
    this.newVolBasedQuoteListeners.splice(
      0,
      this.newVolBasedQuoteListeners.length
    );
  }

  public validateNewVolBasedQuote(
    hedges: any[],
    hedgeMode: HedgeMode
  ): Observable<ValidateMultiHedgeRFQResponse> {
    if (this.tabData.isVolBased) {
      return of({ isAdmissible: true });
    }
    const payload = this.buildNewVolBasedQuote(
      'validateVolBasedRfqHedges',
      hedgeMode,
      hedges,
      null,
      'validate'
    );
    return this.rfqService.tradeService(
      payload
    ) as Observable<ValidateMultiHedgeRFQResponse>;
  }

  private formatNewVolBasedQuote(
    quoteEvent: ClientQuoteEventExtended,
    hedgeMode: HedgeMode,
    hedges: any[]
  ) {
    const payload = this.buildNewVolBasedQuote(
      'volBasedRfqOptionPrice_v2',
      hedgeMode,
      hedges,
      quoteEvent.quote.requestId,
      'quote'
    );
    if (!payload.products.every(p => p != null)) {
      console.warn('Invalid product - aborting pricing');
      return;
    }

    if (!this.rfqService.validateProducts(payload.products)) {
      console.warn('Invalid product - aborting pricing');
      return;
    }
    this.rfqService.tradeService(payload).subscribe({
      next: (response: VolBasedRfqOptionPriceV2Response) => {
        // SMELL, API is typed with different (incompatible)
        // types VolBasedRfqOptionPriceV2Response/Result
        this.lastVolBasedRfqOptionPriceV2Response.set(response as any);
        this.updateQuoteFromV2(quoteEvent, response);
        this.lastNewVolBasedQuoteResponse = response;
        this.updateNewVolBasedQuoteListeners(response);
      },
      error: (error: any) => {
        console.error('formatNewVolBasedQuote', error);
      }
    });
  }

  public updateQuoteFromV2(
    quoteEvent: ClientQuoteEventExtended,
    response: VolBasedRfqOptionPriceV2Response | VolBasedRfqOptionPriceV2Result
  ): void {
    const premiumType =
      this.setupData.premiumValue === 'Spot' ? 'spotPremium' : 'forwardPremium';
    quoteEvent.quote.premiumStyle = response.premiumStyle;
    quoteEvent.quote.premiumCurrency = response.premiumCurrency;
    this.setNewVolPremiums(
      response.portfolioResult,
      response.results.resultPerLeg,
      premiumType,
      quoteEvent,
      response.description
    );
  }

  public formatVolBasedQuote(quoteEvent?: ClientQuoteEventExtended) {
    const products: Product[] = this.legService.createProductsObject(
      this.selectedLegs.map(legComponent => {
        return {
          product: legComponent.form.value,
          attributes: legComponent.leg.attributes
        };
      })
    );

    const validQuote =
      quoteEvent || this.quotes.bids.length || this.quotes.offers.length;

    if (!(validQuote && products.every(validProduct(this.selectedLegs)))) {
      return;
    }

    const hedgeType = getHedgeType(this.setupData.hedge);

    let payload: NewVolsPayload = {
      task:
        this.setupData.premiumValue === 'Spot'
          ? 'bsOptionSpotPrice'
          : 'bsOptionForwardPrice',
      premiumCurrency: this.rfqDetailsDTOMapped?.premiumCurrency,
      applyHedgeNetting: true,
      hedge: { hedgeType },
      products
    };

    const hedgeAmount = formattedNumberToNumber(this.setupData.hedgeAmount);
    if (this.setupData.hedgeDirection && this.setupData.hedgeRate != null) {
      payload.hedge.hedgeDirection = this.setupData.hedgeDirection;
      payload.hedge.hedgeRate = this.setupData.hedgeRate;
      payload.hedge.hedgeAmount = hedgeAmount;
    } else if (this.setupData.hedgeDirection) {
      payload.hedge.hedgeDirection = this.setupData.hedgeDirection;
      payload.hedge.hedgeAmount = hedgeAmount;
    } else if (this.setupData.hedgeRate != null) {
      payload.hedge.hedgeRate = this.setupData.hedgeRate;
    }

    this.rfqService.tradeService<BsOption>(payload).subscribe(response => {
      this.lastBsOption = response;
      const premiumType =
        payload.task === 'bsOptionSpotPrice' ? 'spotPremium' : 'forwardPremium';
      this.setNewVolPremiums(
        response.results.portfolioResult,
        response.results.resultPerLeg,
        premiumType,
        quoteEvent,
        response.description
      );
    });
  }

  public setNewVolPremiums(
    portfolioResult: PortfolioResult | PortfolioResultV2,
    resultsPerLeg: any[],
    premiumType: 'spotPremium' | 'forwardPremium',
    quoteEvent: ClientQuoteEventExtended | ClientQuoteEventV2,
    description: Description,
    indicative = false
  ): { quoteOffer: RfqQuote; quoteBid: RfqQuote } {
    let quoteBid: RfqQuote;
    let quoteOffer: RfqQuote;
    const premium = portfolioResult[premiumType];
    const legPremiums = resultsPerLeg.map(leg => leg.legPremium);
    const legCashPremiums = resultsPerLeg.map(leg => leg.legCashPremium);
    const cashToPremiumStyleDividingAmounts = resultsPerLeg.map(
      leg => leg.cashToPremiumStyleDividingAmount
    );

    const legsInverted = resultsPerLeg.map(leg => leg.isProductInverted);
    if (quoteEvent) {
      const vols = quoteEvent.quote.legs.map(
        leg => ((leg.volBid + leg.volOffer) / 2) * 100
      );
      this.setMidPriceInfo(quoteEvent.quote.premiumAmountMid, vols);
      quoteEvent.quote.description = description;
      if (
        indicative ||
        (quoteEvent.quote.premiumAmountBid != null &&
          this.isQuoteLatest(quoteEvent.quote, 'bid'))
      ) {
        if (!indicative) {
          quoteEvent.quote.premiumAmountBid =
            premium?.bid[this.setupData.premium];
        }
        quoteBid = this.buildQuote(
          quoteEvent.quote,
          'bid',
          portfolioResult,
          legPremiums,
          legCashPremiums,
          cashToPremiumStyleDividingAmounts,
          legsInverted,
          indicative
        );

        this.insertQuote(quoteEvent.quote, quoteBid, 'bid', indicative);
      }

      if (
        indicative ||
        (quoteEvent.quote.premiumAmountOffer != null &&
          this.isQuoteLatest(quoteEvent.quote, 'offer'))
      ) {
        if (!indicative) {
          quoteEvent.quote.premiumAmountOffer =
            premium?.ask[this.setupData.premium];
        }
        quoteOffer = this.buildQuote(
          quoteEvent.quote,
          'offer',
          portfolioResult,
          legPremiums,
          legCashPremiums,
          cashToPremiumStyleDividingAmounts,
          legsInverted,
          indicative
        );
        this.insertQuote(quoteEvent.quote, quoteOffer, 'offer', indicative);
      }
    } else {
      const quoteBid = this.quotes.bids[0];
      const quoteOffer = this.quotes.offers[0];
      if (quoteBid) {
        quoteBid.bidAsk.amount = Math.abs(premium.bid[this.setupData.premium]);
      }

      if (quoteOffer) {
        quoteOffer.bidAsk.amount = Math.abs(
          premium.ask[this.setupData.premium]
        );
      }
    }
    return { quoteOffer, quoteBid };
  }

  private formatDcdQuote(
    eventQuote: ClientEventQuoteExtended,
    side: QuoteSide
  ) {
    if (!eventQuote.premiumAmountBid) {
      return;
    }

    const payload = {
      task: DcdMarginForOptionPrice,
      product: this.legService.createProductsObject(
        this.tabData.legs.map((leg, index) => {
          return {
            product: leg.leg,
            attributes: this.selectedLegs[index].leg.attributes
          };
        })
      )[0],
      vanillaPrice: eventQuote.premiumAmountBid
    };

    this.rfqService
      .tradeService(payload)
      .subscribe(
        (resp: {
          marginCurrency: string;
          margin: number;
          marginSpread: number;
        }) => {
          eventQuote.premiumAmountBid = resp.margin;
          eventQuote.premiumCurrency = resp.marginCurrency;
          eventQuote.marginSpread = resp.marginSpread;
          eventQuote.apiPriceIsFrom = DcdMarginForOptionPrice;
          if (this.isQuoteLatest(eventQuote, side)) {
            const vols = eventQuote.legs.map(
              leg => ((leg.volBid + leg.volOffer) / 2) * 100
            );
            this.setMidPriceInfo(eventQuote.premiumAmountMid, vols);
            const quote = this.buildQuote(
              eventQuote,
              side,
              null,
              null,
              null,
              null,
              null
            );
            this.insertQuote(eventQuote, quote, side, false);
          }
        }
      );
  }

  private isQuoteLatest(
    eventQuote: ClientEventQuoteExtended | ClientEventQuoteExtendedV2,
    side: QuoteSide
  ) {
    const currentQuote =
      side === 'bid'
        ? this.getBidFromProviderId(eventQuote.providerId)
        : this.getOfferFromProviderId(eventQuote.providerId);

    return currentQuote ? currentQuote.timestamp < eventQuote.timestamp : true;
  }

  private buildQuote(
    eventQuote: ClientEventQuoteExtended | ClientEventQuoteExtendedV2,
    side: QuoteSide,
    portfolioResult: PortfolioResult | PortfolioResultV2,
    legPremiums: number[],
    legCashPremiums: number[],
    cashToPremiumStyleDividingAmounts: number[],
    legsInverted: boolean[],
    indicative = false
  ) {
    const rfqShortName = this.tabData.title.productName.shortName;
    const premiumValueToUse =
      side === 'bid'
        ? eventQuote.premiumAmountBid
        : eventQuote.premiumAmountOffer;

    if (
      (this.rfqDetailsDTO || indicative) &&
      premiumValueToUse != null &&
      ((side === 'bid' && this.tabData.positions.bid) ||
        (side === 'offer' && this.tabData.positions.offer))
    ) {
      const quoteReference =
        side === 'bid'
          ? eventQuote.bidQuoteReference
          : eventQuote.offerQuoteReference;

      const hedgeQuote = this.hedgeQuoteMessages().get(
        side === 'bid'
          ? eventQuote.bidQuoteReference
          : eventQuote.offerQuoteReference
      )?.hedges;

      const quote = createQuote(
        rfqShortName,
        premiumValueToUse,
        eventQuote,
        portfolioResult,
        side,
        legPremiums,
        legCashPremiums,
        cashToPremiumStyleDividingAmounts,
        legsInverted,
        this.tabData,
        this.rfqDetailsDTO,
        allFaceCurrenciesTheSame(this.selectedLegs),
        this.selectedLegs,
        this.forwardPointsMultiplier(),
        quoteReference,
        hedgeQuote,
        this.setupData?.execution,
        this.executionContextType(),
        indicative
      );

      if (this.executionContextType() === 'genericMultiHedge') {
        isQuoteOutdated(
          quote,
          this.setupData,
          this.rfqSetupService.setup?.getRawHedges()
        );
      }
      return quote;
    }
    return null;
  }

  private insertQuote(
    eventQuote: ClientEventQuoteExtended | ClientQuoteEventV2['quote'],
    quote: RfqQuote,
    side: QuoteSide,
    indicative: boolean
  ): boolean {
    if (!quote || indicative) {
      return false;
    }
    this.updateProviderState(eventQuote, RFQ_PROVIDER_STATE_DONE); // update the bank response attributes
    const quoteSide = this.quotes[side === 'bid' ? 'bids' : 'offers'];
    let quoteIndex = quoteSide.findIndex(quote => quote.id === eventQuote.id);
    // Update existing quote

    if (
      quoteIndex >= 0 &&
      this.executionContextType() === 'genericMultiHedge' &&
      quote.quoteReference &&
      !quote.hedgeQuotes
    ) {
      this.shelvedQuotes.set(quote.quoteReference, {
        eventQuote,
        quote,
        side,
        indicative
      });
      return false;
    }

    if (this.executionContextType() === 'genericMultiHedge') {
      isQuoteOutdated(
        quote,
        this.setupData,
        this.rfqSetupService.setup?.getRawHedges()
      );
    }

    if (quoteIndex >= 0) {
      if (
        this.executionContextType() === 'genericMultiHedge' &&
        quote.quoteReference &&
        !quote.hedgeQuotes
      ) {
        this.shelvedQuotes.set(quote.quoteReference, {
          eventQuote,
          quote,
          side,
          indicative
        });
        return false;
      }

      const oldQuote = clone(quoteSide[quoteIndex]);
      const newQuote = clone(quote);

      // some fields changes every time so delete before equality check (but we will add back later)
      const ignored: Partial<Record<keyof RfqQuote, any>> = {};
      const fieldsToIgnore: (keyof RfqQuote)[] = [
        'quoteId',
        'timeLimit',
        'timestamp'
      ];
      fieldsToIgnore.forEach(f => {
        ignored[f] = newQuote[f];
        delete oldQuote[f];
        delete newQuote[f];
      });

      // Some values update with higher precision than shown on screen (e.g., 4dp, but 2dp on screen)
      // so quotes might update ("flash") more often than strictly necessary. So we trunc all numbers to 4dp
      // before comparison which is better than nothing.
      // Might need a more sophisticated test to truncate on a per-field basis as the dp's in the template go from 0-4 dp
      // but note that volBased quotes should be already truncated to the relevant precision in hydra.
      const equal = this.tabData.isVolBased
        ? isEqual(oldQuote, newQuote)
        : isEqual(deepCopyNdp(oldQuote), deepCopyNdp(newQuote));
      if (!equal) {
        quoteSide[quoteIndex] = { ...newQuote, ...ignored };
      } else {
        fieldsToIgnore.forEach(f => {
          (quoteSide[quoteIndex] as any)[f] = quote[f];
        });
      }
    } else {
      quoteSide.push(quote);
    }

    if (this._eventQuoteRanking) {
      // sometimes we don't get a new ranking event in certain case's e.g. if there is a new quote
      this.handleQuoteRankEvent(this._eventQuoteRanking);
    }
    return true;
  }

  public setReadOnlyOnFields(value: boolean, fields: string[]): void {
    if (!this.selectedLegs) {
      return;
    }

    this.selectedLegs.forEach(leg => {
      const legValue = leg.form.value;
      fields.forEach(field => {
        const attribute = getAttributeControlFromKey(
          field,
          leg.leg.attributesAsFormControls
        );
        if (attribute) {
          if (
            attribute.key === 'premiumSettlementDate' &&
            legValue.premiumValueType === 'Spot'
          ) {
            return;
          }
          attribute.readOnly = value;
        }
      });
    });
  }

  public removeQuoteByQuoteId(quoteId: number): void {
    // remove a quote by its id
    this.quotes.bids = this.quotes.bids.filter(bid => bid.id !== quoteId);
    this.quotes.offers = this.quotes.offers.filter(
      offer => offer.id !== quoteId
    );
  }

  private removeBankQuote(quoteArray: RfqQuote[], provider: string) {
    const providerIndex = quoteArray.findIndex(
      quote => quote.bank === provider
    );
    if (providerIndex !== -1) {
      quoteArray.splice(providerIndex, 1);
    }
  }

  public getBidFromProviderId(providerId: number): RfqQuote {
    return this.quotes.bids.find(bid => bid.providerId === providerId);
  }

  public getOfferFromProviderId(providerId: number): RfqQuote {
    return this.quotes.offers.find(offer => offer.providerId === providerId);
  }

  public openNotTradingReasonModal(): void {
    if (!this.tabData.isVolBased) {
      return;
    }

    this.rfqOutcomeModal.update(rfqOutcomeModalState => {
      rfqOutcomeModalState.showModal = true;
      rfqOutcomeModalState.requestId = this.rfqDetailsDTO.id;
      return rfqOutcomeModalState;
    });
  }

  public updateQuotesOutdatedStatus(): void {
    [...this.quotes.offers, ...this.quotes.bids].forEach(quote => {
      isQuoteOutdated(
        quote,
        this.setupData,
        this.rfqSetupService.setup?.getRawHedges()
      );
    });
  }

  private hedgeQuotesListener() {
    const resubscribe = () => {
      this.subsink.sink = this.websocketService
        .getListener<RfqHedgeQuote>({
          action: 'apiv2_rfqHedgeQuote',
          take: Infinity
        })
        .subscribe({
          next: r => {
            const payload = r.payload;
            if (
              this.rfqDetailsDTO?.id === payload.message.requestId &&
              this.tabData &&
              this.executionContextType() === 'genericMultiHedge'
            ) {
              const message = payload.message;
              const clonedEvents = new Map([...this.hedgeQuoteMessages()]);
              clonedEvents.set(message.quote.quoteId, message.quote);
              this.hedgeQuoteMessages.set(clonedEvents);
              this.setHedgeQuoteOnQuote(payload.message.quote.quoteId);
            }
          },
          complete: async () => {
            console.log('rfqHedgeQuote subscription completed');
            await sleep(500);
            setTimeout(() => resubscribe());
          },
          error: async err => {
            console.error(err);
            await sleep(500);
            setTimeout(() => resubscribe());
          }
        });
    };

    resubscribe();
  }

  private setHedgeQuoteOnQuote(quoteId: string): void {
    if (this.executionContextType() !== 'genericMultiHedge') {
      return;
    }

    const offer = this.quotes.offers.find(
      offer => offer.quoteReference === quoteId
    );
    const bid = this.quotes.bids.find(bid => bid.quoteReference === quoteId);
    const isGenericMultiHedge =
      this.executionContextType() === 'genericMultiHedge';
    const hedgeQuoteMessage = this.hedgeQuoteMessages().get(quoteId);
    let quoteInserted = false;
    const shelvedQuote = this.shelvedQuotes.get(quoteId);
    if (offer || bid) {
      if (offer) {
        offer.hedgeQuotes = hedgeQuoteMessage.hedges;
        if (isGenericMultiHedge) {
          isQuoteOutdated(
            offer,
            this.setupData,
            this.rfqSetupService.setup?.getRawHedges()
          );
        }
      }

      if (bid) {
        bid.hedgeQuotes = hedgeQuoteMessage.hedges;
        if (isGenericMultiHedge) {
          isQuoteOutdated(
            bid,
            this.setupData,
            this.rfqSetupService.setup?.getRawHedges()
          );
        }
      }
    } else if (shelvedQuote) {
      shelvedQuote.quote.hedgeQuotes = hedgeQuoteMessage.hedges;
      quoteInserted = this.insertShelvedQuote(shelvedQuote);
    }

    if (quoteInserted) {
      const shelvedQuote = this.shelvedQuotes.get(quoteId);
      const vols = shelvedQuote.eventQuote.legs.map(
        leg => ((leg.volBid + leg.volOffer) / 2) * 100
      );
      this.setMidPriceInfo(shelvedQuote.eventQuote.premiumAmountMid, vols);
    }
  }

  private insertShelvedQuote(shelvedQuote: ShelvedQuote): boolean {
    if (shelvedQuote) {
      const existingQuote = [...this.quotes.bids, ...this.quotes.offers].find(
        quote => quote.quoteId === shelvedQuote.quote.quoteId
      );

      if (
        existingQuote &&
        existingQuote.timeLimit > shelvedQuote.quote.timeLimit
      ) {
        return false;
      }
      return this.insertQuote(
        shelvedQuote.eventQuote,
        shelvedQuote.quote,
        shelvedQuote.side,
        shelvedQuote.indicative
      );
    }
    return false;
  }

  private submitRfqRequest(request: {
    urlPath: string;
    payload: any;
  }): Observable<{ result: RFQDetailsDTO }> {
    this.rfqDetailsDTO = null;
    this.shelvedQuotes.clear();
    this.hedgeQuoteMessages.set(new Map([]));
    this.clearRfeRequestInfo();
    if (!this.isDirectRfq) {
      let setupToSave = { ...this.rfqSetupService.setupForm.getRawValue() };
      const rfqSavedSettings =
        this.userStorageService.getItem<RfqSettingsState>(
          RfqSettingsStateKey
        ) || {}; // if there are settings load them

      if (!rfqSavedSettings['rfq-details-settings']) {
        rfqSavedSettings['rfq-details-settings'] = {};
      }
      rfqSavedSettings['rfq-details-settings'][
        this.adminService.medusaAccount.entity
      ] = setupToSave;
      this.userStorageService.setItem(RfqSettingsStateKey, rfqSavedSettings); // save the banks on the browser
    }

    return this.rfqService
      .submitDTORequest(request.urlPath, {
        request: request.payload,
        isPerseus: this.configService.userSettings().usePerseus
      })
      .pipe(
        tap(response => this.recordRFQ(request.payload, response)),
        catchError(err => {
          this.recordRFQ(request.payload, err);
          return throwError(err);
        })
      );
  }

  public setAllAttributesReadOnlyState(readOnlyState: boolean) {
    this.selectedLegs.forEach(legComponent => {
      setAllFieldsReadOnlyState(
        legComponent.leg,
        legComponent.form,
        legComponent.rfqExecutionSettings,
        readOnlyState
      );
      legComponent.rfqExecutionSettings.isPricing = readOnlyState;
    });
  }

  private recordRFQ(medusaRequest: any, medusaResponse: any) {
    try {
      // for auditing purposes, the data is sent to lambda, thence as-is in S3

      const setup = this.rfqSetupService.formatSetup(this.setupData);
      const legs = this.createProductsObject(true);

      const rfqRecord = {
        hydra: {
          legs,
          setup,
          hedges: this.rfqSetupService.setup?.getRawHedges()
        },
        medusaRequest,
        medusaResponse,
        comment: this.rfqCommentSignal()
      };

      // fire and forget
      this.rfqService.recordSubmitRequest(rfqRecord);
      const requestId = medusaResponse.result?.id;
      if (requestId) {
        this.submitSetupEvent(requestId, {
          isNew: true,
          legs,
          change: undefined
        });
      }
    } catch (e) {
      console.error('Error recording RFQ', e);
    }
  }

  public submitPricingUpdateRequestEvent(
    hedgeQuoteEvent: any,
    hedgeBooking: HedgeQuoteMessage
  ): Observable<any> {
    const setup = this.rfqSetupService.formatSetup(
      this.rfqSetupService.setup.form.getRawValue()
    );
    const payload = {
      requestId: this.rfqDetailsDTO.id,
      setup: setup,
      hedgeQuoteEvent: hedgeQuoteEvent,
      executedInvertedSide: false,
      hedgeBooking,
      hydraLegs: this.createProductsObject(true)
    };
    return this.rfqService.submitPricingUpdateRequestEvent(payload);
  }

  public submitSetupEvent(
    requestId: any,
    { isNew, change, legs }: SetupEventOptions
  ): void {
    const setup = this.rfqSetupService.formatSetup(
      this.rfqSetupService.setup.form.getRawValue()
    );
    const isGenericMultiHedge =
      this.executionContextType() === 'genericMultiHedge';

    const hedges = this.rfqSetupService.setup?.getHedges().hedges;

    if (isGenericMultiHedge) {
      const hedgeComponents =
        this.rfqSetupService.setup.multiHedgeGroups?.first?.hedgeComponents;
      if (hedgeComponents?.some(hedgeComponent => !hedgeComponent.form.valid)) {
        return;
      }

      if (setup.hedge === 'Forward Hedge') {
        hedgeComponents?.forEach((hedge, index) => {
          if (!hedge.form.get('amount').dirty) {
            delete hedges[index].amount;
          }
        });
      }

      if (hedges.length === 1) {
        delete hedges[0].execution;
      } else if (hedges.length > 1) {
        delete setup.execution;
      }
    }

    setup.hedges = hedges;
    setup.legs = legs ?? this.createProductsObject(true);
    setup.isNew = isNew;
    setup.change = change;
    this.rfqService.submitSetupEvent(requestId, setup).subscribe();
  }

  private revertLegControlAttributes(): void {
    if (!this.legsAttributeControls) {
      return;
    }

    this.selectedLegs.forEach((legComponent: LegComponent, index: number) => {
      const legValue = legComponent.form.value;
      const legAttributesClone = this.legsAttributeControls[index];
      legComponent.allAttributesBase.forEach(
        (control, controlIndex: number) => {
          Object.assign(control, legAttributesClone[controlIndex]);
        }
      );
      legComponent.rfqExecutionSettings.isPricing = false;
      setAttributeReadOnly(
        'premiumSettlementDate',
        legValue.premiumValueType === 'Spot',
        legComponent.leg,
        legComponent.form,
        legComponent.rfqExecutionSettings
      );
    });

    this.legsAttributeControls = null;
  }

  private get isWhiteLabelAccountsEnabled(): boolean {
    return !!this.configService.userSettings().parent[
      'rfq:whiteLabelAccounts:enabled'
    ];
  }

  private setSetupData() {
    this.rfqDetailsDTOMapped = clone(this.rfqDetailsDTOMappedInitial);
    this.rfqDetailsDTOMapped.premiumDelivery = this.isDirectRfq
      ? this.selectedLegs[0].form.get('premiumType').value
      : this.setupData.premiumValue;

    this.rfqDetailsDTOMapped.premiumCurrency = this.isDirectRfq
      ? this.selectedLegs[0].form.get('premiumCurrency').value
      : this.setupData.premium;

    if (!this.isDirectRfq) {
      let account = {};
      const realAccount = this.entityMapperService.getRealEntity(
        this.setupData.account
      );
      if (this.isWhiteLabelAccountsEnabled) {
        this.rfqDetailsDTOMapped.whiteLabelAccount = realAccount;
        // White Label accounts use a fixed account
        account = {
          id: this.configService.userSettings().parent[
            'rfq:whiteLabelAccounts:account_id'
          ]
        };
      } else {
        account = this.accounts().find(account => account.name === realAccount);
      }

      this.rfqDetailsDTOMapped.account = pick(account, ['id', 'name']);

      const pbValue = this.rfqSetupService.getPrimeBrokerFromForm();

      const primeBroker = this.primeBrokers.primeBrokerList.find(
        broker => broker.shortName === pbValue || broker.name === pbValue
      );
      if (primeBroker) {
        this.rfqDetailsDTOMapped.primeBroker = pick(primeBroker, [
          'id',
          'name'
        ]);
      }
    }

    if (this.tabData.isVolBased || this.isDirectRfq) {
      this.rfqDetailsDTOMapped.hedge = 'None';
    } else {
      switch (this.setupData.hedge) {
        case 'Spot':
        case 'Custom Spot':
          this.rfqDetailsDTOMapped.hedge = 'DeltaSpot';
          break;
        case 'Forward':
        case 'Custom Forward':
          this.rfqDetailsDTOMapped.hedge = 'DeltaForward';
          break;
        case 'None':
          this.rfqDetailsDTOMapped.hedge = 'None';
          break;
      }
    }

    this.rfqDetailsDTOMapped.providers = this.isDirectRfq
      ? [this.rfqService.providers[0]]
      : this.setupData?.banks;

    if (
      !this.isDirectRfq &&
      this.setupData.hedgeRate != null &&
      this.setupData.hedgeRate !== ''
    ) {
      this.rfqDetailsDTOMapped.hedgeRate = formattedNumberToNumber(
        this.setupData.hedgeRate
      );
    }
    this.rfqDetailsDTOMapped.premiumStyle = this.getDTOPremiumStyle();
  }

  public setForwardPointsMultiplier(): void {
    const { base, term } = this.tabData.title.quotePair;
    this.forwardPointsMultiplier.update(
      _ =>
        +this.productsConfigService.cutOffTimesAndFixingSources.currencyPairInfo.find(
          currencyInfo => currencyInfo.currencyPair === base + term
        )?.forwardPointsMultiplier
    ); // get forwardPointsMultiplier that is going to be used)
  }

  public getCurrencyPair(): string {
    if (!this.tabData.title.quotePair) {
      return null;
    }

    const { base, term } = this.tabData.title.quotePair;
    return base + term;
  }

  public isCashSettled() {
    return !this.isMedusaDeliverable();
  }

  public isMedusaDeliverable(): boolean {
    const ccy = this.getCurrencyPair();
    const pair = this.userSettings.currencyPairs.find(
      p => p.currencyPair === ccy
    );
    return pair?.isMedusaDeliverable === 'true';
  }

  public recordRfqOutcome(status: RfqOutcome, requestId?: number): void {
    if (!this.tabData.isVolBased) {
      return;
    }
    this.rfqService
      .recordRfqOutcome(requestId || this.rfqDetailsDTO.id, status)
      .subscribe();
  }

  private mapGeneratedDTO(
    request: RFQDetailsDTO, // the create request
    formattedLegs: OptionLegDTO[], //legs for the dto
    medusaCode: RFQDetailsDTO['market'] | OptionLegDTO['fixingSource']
  ): RFQDetailsDTO {
    request.client = {
      id: 0,
      name: '',
      shortName: null,
      tradesSpot: false
    };

    const shortName = this.tabData.title.productName.shortName;

    if (
      shortName === 'oneTouch' ||
      shortName === 'instantOneTouch' ||
      shortName === 'doubleNoTouch' ||
      shortName === 'doubleOneTouch' ||
      shortName === 'digital'
    ) {
      request.cashSettled = this.isCashSettled();
    }

    const medusaType = this.medusaCodeAndType.medusaType;
    formattedLegs.forEach(dtoLeg => {
      dtoLeg.fixingSource =
        medusaType === 'fixingSource'
          ? (medusaCode as OptionLegDTO['fixingSource'])
          : undefined;
    });

    if (medusaType === 'fixingSource') {
      delete request.market; // no market for tpfs
    } else if (medusaType === 'market') {
      request.market = medusaCode as RFQDetailsDTO['market'];
    }

    request.strategy = getStrategy(shortName);
    request.legs = formattedLegs;

    return request;
  }

  public getDTOPremiumStyle(): RFQDetailsDTO['premiumStyle'] {
    const dto = this.rfqDetailsDTOMapped;
    const strategy = getStrategy(this.tabData.title.productName.shortName);
    const { term, base } = this.tabData.title.quotePair;
    const faceCurrency = dto.legs[0].faceCurrency;
    const premiumCurrency = dto.premiumCurrency;
    const useAmount = strategy === 'TargetProfitForward';

    if (base === premiumCurrency && faceCurrency === base) {
      return 'PercentBase';
    } else if (term === premiumCurrency && faceCurrency === base) {
      return useAmount ? 'AmountTerm' : 'PipsTerm';
    } else if (term === premiumCurrency && faceCurrency === term) {
      return 'PercentTerm';
    } else {
      return useAmount ? 'AmountBase' : 'PipsBase';
    }
  }

  public blockTabError(text: string) {
    this.changeTabTile(RFQ_STATUS_BLOCKED, null);
    setTimeout(() => {
      this.rfqService.emitErrorMessage(text, undefined, {
        source: 'blockTabError'
      });
    });
  }

  public createProductsObject(getRawValue = false): any[] {
    return this.legService.createProductsObject(
      this.selectedLegs.map(legComponent => {
        return {
          product: getRawValue
            ? legComponent.form.getRawValue()
            : legComponent.form.value,
          attributes: legComponent.leg.attributes
        };
      })
    );
  }

  public get setFormattedLegs(): Observable<RFQDetailsDTO[]> {
    try {
      this.formattedDtoLegs = this.configService.userSettings().usePerseus
        ? this.createProductsObject()
        : this.formatAllLegsForDto();
      return of(this.formattedDtoLegs);
    } catch (e) {
      console.error('setFormattedLegs', e);
      return throwError('An issue occurred during the mapping of legs to DTO');
    }
  }

  public setDetailsDtoMap(): void {
    this.rfqDetailsDTOMapped = this.mapGeneratedDTO(
      clone(this.createDtoResponse),
      this.formattedDtoLegs,
      this.medusaCodeAndType.medusaCode
    );
    this.rfqDetailsDTOMappedInitial = clone(this.rfqDetailsDTOMapped);
  }

  public get createDto(): Observable<{
    result: RFQDetailsDTO;
  }> {
    return this.rfqService
      .createDTO(
        rfqProductApiName(
          this.formattedDtoLegs,
          this.tabData.title.productName.shortName
        ),
        {
          quotePair: this.tabData.title.quotePair
        }
      )
      .pipe(
        catchError(_ => {
          return throwError('Failed to create leg');
        })
      );
  }

  public marketCodeFixingSource(): Observable<MedusaCodeAndType> {
    let products: RfqTabLeg[];
    const productName = this.tabData.title.productName.shortName;

    switch (productName) {
      case 'dcd':
        products = this.tabData.legs.map(rfqTabLeg => rfqTabLeg.childLeg);
        break;
      default:
        products = this.legService.createProductsObject(
          this.tabData.legs.map((rfqTabLeg, index: number) => {
            return {
              product: rfqTabLeg.leg,
              attributes: this.selectedLegs[index].leg.attributes
            };
          })
        );
        break;
    }

    return this.rfqService
      .getMedusaMarketCodeOrFixingSource({
        products,
        task: 'getMedusaMarketCodeOrFixingSource'
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          return throwError(
            err.error?.errorMessage ||
              'Failed to get market code or fixing source'
          );
        })
      );
  }

  public discountFactor(product: KeyValue): Observable<DiscountFactorResponse> {
    return this.rfqService.getDiscountFactor({
      task: 'getDefaultPricingCurrencySpotToForwardDiscountFactor',
      product
    });
  }

  public initRfqSubmitRequest() {
    this.frozenQuotes = null;
    this.rfqDetailsDTO = null;
    this.quotes.bids = [];
    this.quotes.offers = [];
    this.rfqRunning.update(_ => true);
    this.legsAttributeControls = clone(
      this.selectedLegs.map(x => x.allAttributesBase)
    );
    this.setSetupData();
    if (!this.isDirectRfq) {
      this.wipeAllBanks();
      this.setAllAttributesReadOnlyState(true);
    }

    const payload = this.rfqDetailsDTOMapped;
    // legacy whitelabel code (LODH) has an extra flag "indicative" which is in the
    // wrong state for RFQ - force to false
    if (payload.indicative != null) {
      payload.indicative = false;
    }
    const flagDCD = this.flagDCD();
    if (flagDCD !== undefined) {
      payload.is_dcd = flagDCD;
    }

    // the DTO needs to be created from the back end and then mapped by the front end before sending to the API.
    return this.submitRfqRequest({
      urlPath: rfqProductApiName(
        this.rfqDetailsDTOMapped.legs,
        this.tabData.title.productName.shortName
      ),
      payload
    }).pipe(
      tap((response: { result: any }) => {
        if (
          response.result['@type'] === 'RFQDetailsDTO' &&
          response.result.id != null
        ) {
          this.rfqDetailsDTO = response.result;
        }
      })
    );
  }

  private flagDCD(): boolean | undefined {
    const user = this.adminService.medusaUser;
    const url = user ? user.url : '';
    const isProxy = url.indexOf('proxy') > -1;
    if (!isProxy) {
      return undefined;
    }
    const isDCD = this.selectedLegs[0].leg.productType === 'newdcd';
    return !!user?.flag_dcds && isDCD;
  }

  private formatAllLegsForDto(): RFQDetailsDTO[] {
    const productName = this.tabData.title.productName.shortName;
    const rfqTabLegs: RfqTabLegs[] = this.tabData.legs;
    const isFirstGenExotic = this.tabData.isFirstGenExotic;
    // some products have 1 hydra leg, but need to be seen on medusa as two
    let legs = [];
    rfqTabLegs.forEach(rfqTabLeg => {
      switch (rfqTabLeg.shortName) {
        case 'straddle':
          legs.push(
            {
              ...rfqTabLeg.leg,
              call: true,
              notionalFieldsToUse: rfqTabLeg.fieldsToBeEdited,
              legShortName: rfqTabLeg.shortName
            },
            {
              ...rfqTabLeg.leg,
              call: false,
              notionalFieldsToUse: rfqTabLeg.fieldsToBeEdited,
              legShortName: rfqTabLeg.shortName
            }
          );
          break;
        case 'riskreversal':
        case 'strangle':
          // at times we have fields such as notional1 and notional2 on the same leg
          // they need to be sent in 2 seperate legs
          legs.push(
            {
              ...rfqTabLeg.leg,
              notional2: null, // notional2 shouldn't exist on the first leg
              counterNotional: rfqTabLeg.leg.counterNotional1,
              optionType: rfqTabLeg.leg.optionType1,
              strike: rfqTabLeg.leg.strike1,
              position: rfqTabLeg.leg.position1 || rfqTabLeg.leg.position,
              notionalFieldsToUse: rfqTabLeg.fieldsToBeEdited,
              legShortName: rfqTabLeg.shortName
            },
            {
              ...rfqTabLeg.leg,
              notional1: null, // notional1 shouldn't exist on the 2nd leg
              counterNotional: rfqTabLeg.leg.counterNotional2,
              optionType: rfqTabLeg.leg.optionType2,
              strike: rfqTabLeg.leg.strike2,
              position: rfqTabLeg.leg.position2 || rfqTabLeg.leg.position,
              notionalFieldsToUse: rfqTabLeg.fieldsToBeEdited,
              legShortName: rfqTabLeg.shortName
            }
          );
          break;
        case 'newdcd':
          legs.push({
            ...rfqTabLeg.childLeg,
            notionalFieldsToUse: {
              notional:
                rfqTabLeg.childLeg.faceCurrency ===
                rfqTabLeg.childLeg?.cross?.substring(0, 3)
                  ? 'counterNotional'
                  : 'notional'
            },
            legShortName: 'vanilla'
          });
          break;
        case VANILLA_STRIP:
          const formattedLeg = this.formatVanillaStripLeg(
            rfqTabLeg.leg,
            rfqTabLeg.leg.schedule[0],
            rfqTabLeg.shortName
          );

          if (formattedLeg) {
            formattedLeg.schedule =
              rfqTabLeg.leg.schedule?.map(scheduleRow => {
                const updatedSchedule = {
                  expiryDate: scheduleRow.expiryDate,
                  settlementDate: scheduleRow.settlementDate,
                  notional: scheduleRow.notional,
                  strike: scheduleRow.strike
                };
                return updatedSchedule;
              }) || [];

            legs.push(formattedLeg);
          }
          if (legs.some(leg => leg.notional === undefined)) {
            console.error("Some legs are missing 'notional'!");
          }
          break;
        default:
          legs.push({
            ...rfqTabLeg.leg,
            notionalFieldsToUse: rfqTabLeg.fieldsToBeEdited,
            legShortName: rfqTabLeg.shortName
          });
          break;
      }
    });
    return getRfqDto(legs, productName, isFirstGenExotic);
  }

  public get premiumValue(): string {
    return this.setupData.premiumValue;
  }

  public get setupData() {
    return this.rfqSetupService.setupForm
      ? this.rfqSetupService.setupForm.getRawValue()
      : null;
  }

  public get setupValidity() {
    return this.rfqSetupService.setup
      ? this.rfqSetupService.setup.valid
      : false;
  }

  public formatVanillaStripLeg(
    leg: RfqTabLeg,
    scheduleRow: Record<string, any>,
    legShortName: string
  ): RfqTabLeg {
    const { expiryDate, settlementDate, strike, notional } = scheduleRow;

    return {
      ...leg,
      expiryDate,
      settlementDate,
      strike,
      notional,
      notionalFieldsToUse: leg.notionalFieldsToUse,
      schedule: [],
      legShortName
    };
  }

  public sendRfeAction(bank: RfqQuote): Observable<KeyValueResponse> {
    this.animateQuotes = false; // don't show quote animations
    this.frozenQuotes = cloneDeep(this.quotes);
    this.rfeRequestInfo = {
      bank: bank.bank,
      providerId: bank.providerId,
      type: bank.type,
      status: RFQ_QUOTE_CONFIRM_RFE_SENT
    };
    setTimeout(() => this.maybeAnimateQuotes());
    this.quotesBeingDisplayed.bids.forEach(bid => {
      bid.status =
        bid.quoteId === bank.quoteId
          ? RFQ_QUOTE_CONFIRM_RFE_SENT
          : RFQ_QUOTE_CONFIRM_RFE_NOT_SENT;
    });

    this.quotesBeingDisplayed.offers.forEach(offer => {
      offer.status =
        offer.quoteId === bank.quoteId
          ? RFQ_QUOTE_CONFIRM_RFE_SENT
          : RFQ_QUOTE_CONFIRM_RFE_NOT_SENT;
    });
    return this.rfqService.acceptQuoteById({ quoteId: bank.quoteId });
  }

  public executionContextType(): string {
    return this.tabData.executionContext.executionTypes[0].shortName;
  }

  private wipeAllBanks(): void {
    if (!this.isDirectRfq && this.setupData) {
      this.selectedBanksState.update(state => {
        state.forEach(bank => {
          bank.status = RFQ_PROVIDER_STATE_UNQUOTED;
          bank.quoteId = null;
        });
        return state;
      });
    }
  }

  public adjustQuoteForMargin(quoteEvent?: ClientQuoteEventExtended): void {
    const payload = this.adjustQuoteForMarginPayload(quoteEvent);
    if (!payload) {
      return;
    }

    this.adjustQuoteForMarginApi(payload).subscribe({
      next: respEvent => {
        respEvent.adjustedClientQuoteEvent.quoteAdjusted = true;
        respEvent.adjustedClientQuoteEvent.originalQuote =
          payload.clientQuoteEvent.quote;
        this.handleEvents([respEvent.adjustedClientQuoteEvent]);
      }
    });
  }

  public adjustQuoteForMarginPayload(
    quoteEvent?: ClientQuoteEventExtended
  ): AdjustQuoteForMarginPayload {
    if (
      !this.tabData.useWhiteLabelMargin ||
      !this.selectedLegs.every(x => x.form.valid)
    ) {
      return null;
    }
    const clientQuoteEvent =
      quoteEvent ||
      Array.from(this.lastQuoteEvents())[this.lastQuoteEvents().size - 1]?.[1];

    if (!clientQuoteEvent) {
      return null;
    }

    const clonedClientQuoteEvent = clone(clientQuoteEvent);
    delete clonedClientQuoteEvent.processed;
    delete clonedClientQuoteEvent.quoteAdjusted;

    return {
      products: this.createProductsObject(),
      clientQuoteEvent: clonedClientQuoteEvent,
      RFQDetailsDTO: this.rfqDetailsDTO
    };
  }

  public adjustQuoteForMarginApi(
    payload: AdjustQuoteForMarginPayload
  ): Observable<AdjustQuoteForMargin> {
    return this.rfqService.adjustQuoteForMargin(payload);
  }

  public cancelRfq(sendCancelMsg = true): void {
    this.revertLegControlAttributes();
    this.lastQuoteEvents.set(new Map());
    this.onRfqSessionEnded.next();
    this.animateQuotes = false;
    this.frozenQuotes = cloneDeep(this.quotesBeingDisplayed);
    setTimeout(() => this.maybeAnimateQuotes());

    if (!sendCancelMsg || !this.rfqDetailsDTO) {
      this.onRfqCancellation(null);
      return;
    }

    this.rfqService
      .cancelRfq({ requestId: this.rfqDetailsDTO.id })
      .subscribe(response => {
        if (response.result) {
          this.onRfqCancellation(RFQ_STATUS_CANCELLED);
        }
      });
  }

  private onRfqCancellation(rfqStatus: RfqStatus): void {
    this.rfqDetailsDTO = null;
    this.changeTabTile(rfqStatus, null);
    this.wipeAllBanks();
  }

  public calculateSpotDate(): Observable<string> {
    if (!this.tabData.title.quotePair) {
      return throwError(null);
    }
    const currentDate = dayjs().utc().startOf('day').valueOf();
    const payLoad = {
      currentDate,
      quotePair: this.tabData.title.quotePair
    };

    return this.rfqService
      .calculateSpotDate(payLoad)
      .pipe(map(response => dayjs.utc(response.result).format('D MMM YYYY')));
  }

  private setMidPriceInfo(premiumAmountMid: number, vols: number[]): void {
    if (!this.tabData.executionContext?.useSingleProviderDetailedView) {
      return;
    }

    const leg0 = this.selectedLegs[0].form.value;
    const premiumCurrency = this.setupData.premium;
    const quotePair = this.tabData.title.quotePair;
    const notional = getReferenceNotional(
      this.selectedLegs[0].leg.productType,
      this.rfqDetailsDTO.legs[0].eventSchedule,
      quotePair.base,
      quotePair.term,
      {
        currency: leg0.currency,
        notional: leg0.notional,
        notional1: leg0.notional1
      }
    );

    const counterNotional = getReferenceCounterNotional(
      this.selectedLegs[0].leg.productType,
      this.rfqDetailsDTO.legs[0].eventSchedule,
      quotePair.base,
      quotePair.term,
      {
        currency: leg0.currency,
        counterNotional: leg0.counterNotional,
        counterNotional1: leg0.counterNotional1
      }
    );
    const forwardPointsMultiplier = this.forwardPointsMultiplier();

    if (notional == null || counterNotional == null) {
      return;
    }

    const midPrices = midPrice(
      premiumAmountMid,
      premiumCurrency,
      quotePair.base,
      quotePair.term,
      notional,
      counterNotional,
      forwardPointsMultiplier,
      vols
    );

    if (this.executionContextType() === 'genericMultiHedge') {
      const quotes = Array.from(this.lastQuoteEvents().values());
      if (quotes.length > 0) {
        const { spotBid, spotOffer } = quotes[0].quote.legs[0];
        const cross = this.getCurrencyPair();
        const dp = this.productsConfigService.decimalPlacesForCross(cross);
        midPrices.spotMid = formatNumber(
          (spotBid + spotOffer) / 2,
          'en-GB',
          `1.${dp}-${dp}`
        );
      }
    }

    this.midPriceInfo.set(midPrices);
  }
}
